/*
 * Actions often triggered by a button click
 */

/**
 * @typedef {{ home: {teamStats: object, playerStats: array}, away: {teamStats: objecct, playerStats: array}}} Stats
 * @typedef {("home"|"away")} Side
 *
 */

import _ from "lodash";
import { postPlayChanges } from "./postPlay";
import { getPreviousPlay } from "games/utils";
/*
 * Re-calculate values in each play
 * @params {array} plays
 * @params {object} game
 * @returns {array}
 */
const calculatePlays = (plays, game) => {
  return plays.reduce((acc, play, i, arr) => {
    const previousPlay = getPreviousPlay(acc, i);
    const transformedPlay = {
      ...play,
      ...(i !== 0 && postPlayChanges(play, previousPlay, game))
    };

    return [...acc, transformedPlay];
  }, []);
};

/*
 * Set values in object to null if the condition function returns true
 * If nothing is passed in the condition param, no values will get set to null
 * @param {object}
 * @param {function} condition - only set value to null if this condition is true
 * @returns {object}
 */
const setNullIf = (object, condition) => {
  return Object.keys(object).reduce((obj, key) => {
    const value = condition(key, object[key]) ? null : object[key];
    return {
      ...obj,
      [key]: value
    };
  }, {});
};

/*
 * Set values in object with specific "allowed" keys to null
 * @param {object}
 * @param {string[]} allowed - clear only values with these keys
 * @returns {object}
 * @example setNullIfAllowed({totalPoints: 10, id: 123}, ["id"])
 */
const setNullIfAllowed = (object, allowed = []) => {
  const condition = (key) => allowed.includes(key);
  return setNullIf(object, condition);
};

/*
 * Set values in object to null if their value is an empty string
 * @param {object}
 * @returns {object}
 * @example setNullIfEmptyString({totalPoints: ""})
 */
const setNullIfEmptyString = (object) => {
  const condition = (key, value) => value === "";
  return setNullIf(object, condition);
};

/*
 * Sets all values in an object to null, except for values of exceptions
 * If nothing is passed in the exceptions param, all values will get set to null
 * @param {object} stats
 * @param {string[]} exceptions
 * @returns {object}
 */
const setNull = (object, exceptions = []) => {
  if (!object) return;

  return Object.keys(object).reduce((obj, key) => {
    const value = exceptions.includes(key) ? object[key] : null;
    return {
      ...obj,
      [key]: value
    };
  }, {});
};

/*
 * @param {Stats} stats
 * @param {Side} side
 * @param {function} func
 * @param {string[]} keys
 * @returns {array}
 */
const setNullPlayerStatsBase = ({ stats, side, func, keys }) => {
  const allPlayerStats = _.get(stats, [side, "playerStats"]);
  return allPlayerStats.map((player) => func(player, keys));
};

/*
 * @param {Stats} stats
 * @param {Side} side
 * @param {string[]} exceptions
 * @returns {array}
 */
const setNullPlayerStats = ({ stats, side, exceptions = [] }) => {
  return setNullPlayerStatsBase({
    stats,
    side,
    func: setNull,
    keys: [...exceptions, "id", "name"]
  });
};

/*
 * @param {Stats} stats
 * @param {Side} side
 * @param {function} func
 * @param {string[]} keys
 * @returns {object}
 */
const setNullTeamStatsBase = ({ stats, side, func, keys }) => {
  const teamStats = _.get(stats, [side, "teamStats"]);
  return func(teamStats, keys);
};

/*
 * @param {Stats} stats
 * @param {Side} side
 * @param {string[]} allowed
 * @returns {object}
 */
const setNullTeamStatsIfAllowed = ({ stats, side, allowed }) => {
  return setNullTeamStatsBase({
    stats,
    side,
    func: setNullIfAllowed,
    keys: allowed
  });
};

/*
 * @param {Stats} stats
 * @param {Side} side
 * @param {string[]} exceptions
 * @returns {object}
 */
const setNullTeamStats = ({ stats, side, exceptions }) => {
  return setNullTeamStatsBase({
    stats,
    side,
    func: setNull,
    keys: exceptions
  });
};

export {
  calculatePlays,
  setNullIfAllowed,
  setNullIfEmptyString,
  setNullTeamStatsIfAllowed,
  setNullTeamStats,
  setNullPlayerStats,
  setNull
};
